import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { downloadExport } from "../lib/api";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  pending?: boolean;
}

const EXPORT_PATH_PREFIX = "/v1/exports/";

export default function MessageBubble({
  message,
  token,
}: {
  message: ChatMessage;
  token: string;
}) {
  if (message.role === "system") {
    return (
      <div className="my-2 text-center text-xs text-slate-400">{message.content}</div>
    );
  }

  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white"
            : "bg-white text-slate-800 border border-slate-200"
        }`}
      >
        {message.pending ? (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Dot /> <Dot delay="150ms" /> <Dot delay="300ms" />
          </span>
        ) : isUser ? (
          <span className="whitespace-pre-wrap">{message.content}</span>
        ) : (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // agent 回复中的导出链接（/v1/exports/{id}）需要带 Bearer token
                // 才能下载，普通 <a> 带不了鉴权头，改渲染为 fetch 下载按钮
                a: ({ href, children }) =>
                  href?.startsWith(EXPORT_PATH_PREFIX) ? (
                    <ExportDownloadButton path={href} token={token}>
                      {children}
                    </ExportDownloadButton>
                  ) : (
                    <a href={href} target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function ExportDownloadButton({
  path,
  token,
  children,
}: {
  path: string;
  token: string;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<"idle" | "downloading" | "error">("idle");

  async function handleClick() {
    setState("downloading");
    try {
      await downloadExport(token, path);
      setState("idle");
    } catch {
      setState("error");
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={state === "downloading"}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
    >
      {state === "downloading" ? "下载中…" : children}
      {state === "error" && (
        <span className="text-red-500">（下载失败，文件可能已过期）</span>
      )}
    </button>
  );
}

function Dot({ delay = "0ms" }: { delay?: string }) {
  return (
    <span
      className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current"
      style={{ animationDelay: delay }}
    />
  );
}
